import React, { useState } from 'react';
import { Button } from './ui/button';
import { Slider } from './ui/slider';
import { Play, Pause, SkipBack, RotateCcw, Zap } from 'lucide-react';
import { formatTime } from '../utils/audioPlayer';
import Waveform from './Waveform';
import { formatModeLabel } from '../utils/formatters';
import LoopButtons from './LoopButton';
import LoopRollButtons from './LoopRollButtons';
import { saveTrackAnalysis } from '../utils/api';

const DeckControls = ({ 
  deckId,
  track,
  isPlaying,
  currentTime,
  duration,
  pitch,
  volume = 100,
  onPlayPause,
  onPitchChange,
  onVolumeChange,
  onSeek,
  onCue,
  onLoop,
  onSync,
  isActive,
  pitchInfo = null, // Smart Mix v2: pitch and BPM info
  currentMode = null, // Mode de la partie actuelle
  loopSize = null, // Current loop size (1, 2, 4, 8, 16, 32, 64) or null
  onLoopChange = null, // Callback when loop changes
  loopRollSize = null, // Current loop-roll size (active when holding)
  onLoopRollStart = null, // Callback when loop-roll starts (mousedown)
  onLoopRollEnd = null // Callback when loop-roll ends (mouseup)
}) => {
  const [cuePoint, setCuePoint] = useState(null);
  
  // Handler for Play/Pause button
  const handlePlayPauseClick = () => {
    console.log(`🖱️ BUTTON CLICKED! Deck ${deckId}`, { 
      track: track?.title || 'NO TRACK',
      isPlaying,
      hasCallback: !!onPlayPause 
    });
    if (onPlayPause) {
      onPlayPause();
    } else {
      console.error(`❌ onPlayPause callback is missing for Deck ${deckId}`);
    }
  };

  const handleSetCue = () => {
    setCuePoint(currentTime);
    // onCue && onCue(currentTime);  // Advanced CUE for V2
  };

  const handleJumpToCue = () => {
    if (cuePoint !== null) {
      onSeek && onSeek(cuePoint);
    } else {
      // MVP: If no cue point set, CUE button returns to start
      onCue && onCue(0);
    }
  };

  const progress = duration ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      className={`border rounded-2xl p-3 sm:p-4 md:p-6 transition-all ${
        isActive 
          ? deckId === 'A' 
            ? 'bg-blue-500/10 border-blue-500/30' 
            : 'bg-purple-500/10 border-sunset-orange/30'
          : 'bg-dark-card/50 border-sunset-orange/20'
      }`}
      role="article"
      aria-label={`Platine ${deckId}${isActive ? ' - Active' : ''}`}
    >
      {/* Header - Responsive */}
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <div className="flex items-center gap-2">
          <div 
            className={`w-3 h-3 rounded-full ${
              isPlaying ? 'bg-green-500 animate-pulse' : 'bg-slate-600'
            }`}
            role="status"
            aria-label={isPlaying ? 'En lecture' : 'En pause'}
          ></div>
          <h3 
            className={`text-base sm:text-lg font-semibold ${
              isActive 
                ? deckId === 'A' ? 'text-blue-400' : 'text-purple-400'
                : 'text-sunset-orange'
          }`}>
            DECK {deckId}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {currentMode && (
            <span className="px-2 py-1 rounded-full text-xs bg-purple-500/20 text-sunset-orange border border-sunset-orange/30">
              {formatModeLabel(currentMode)}
            </span>
          )}
          {isActive && (
            <span className="px-2 py-1 rounded-full text-xs bg-green-500/20 text-green-300 border border-green-500/30">
              LIVE
            </span>
          )}
        </div>
      </div>

      {/* Track Info */}
      {track ? (
        <div className="mb-6">
          <h4 className="text-xl font-bold text-white truncate mb-1">{track.title}</h4>
          <p className="text-sm text-sunset-orange/80 truncate">{track.artist}</p>
          {/* SMART MIX V2: BPM Display */}
          {pitchInfo && pitchInfo.originalBPM && (
            <div className="mt-2 flex items-center gap-2 text-xs">
              <span className="px-2 py-1 rounded bg-purple-500/20 text-sunset-orange font-mono">
                {pitchInfo.currentBPM} BPM
              </span>
              {Math.abs(pitchInfo.pitchPercent) > 0.1 && (
                <span className="text-purple-400/60">
                  (original: {pitchInfo.originalBPM} BPM)
                </span>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="mb-6 text-center py-8 text-sunset-orange/40">
          En attente...
        </div>
      )}

      {/* Waveform / Progress */}
      {track && (
        <div className="mb-6">
          {/* PHASE 3: Professional Waveform with Beat Grid */}
          <Waveform
            trackId={track.id}
            preloadedWaveform={track.waveform_data}
            currentTime={currentTime}
            duration={duration}
            bpm={track.bpm}
            onSeek={onSeek}
            onWaveformGenerated={async (trackId, analysisData) => {
              // Save waveform to database for instant loading next time
              try {
                await saveTrackAnalysis(trackId, {
                  waveform: analysisData.waveform,
                  bpm: analysisData.bpm,
                  duration: Math.round(analysisData.duration)
                });
                console.log('✅ Waveform saved to database');
              } catch (error) {
                console.error('Failed to save waveform:', error);
              }
            }}
            height={100}
            className="mb-2"
          />

          {/* Time display */}
          <div className="flex justify-between text-sm text-sunset-orange/60 mb-2">
            <span>{formatTime(currentTime)}</span>
            <span className="text-sunset-orange/80 font-mono">
              {formatTime(duration - currentTime)}
            </span>
            <span>{formatTime(duration)}</span>
          </div>

          {/* Progress slider */}
          <Slider
            value={[progress]}
            onValueChange={(value) => {
              const newTime = (value[0] / 100) * duration;
              onSeek && onSeek(newTime);
            }}
            className="w-full"
          />
        </div>
      )}

      {/* Transport Controls - Accessible & Touch-friendly */}
      <div className="flex items-center justify-center gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Button
          onClick={handleJumpToCue}
          size="lg"
          variant="outline"
          className="w-12 h-12 sm:w-14 sm:h-14 min-w-[44px] min-h-[44px] rounded-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 touch-manipulation"
          disabled={cuePoint === null}
          title="Aller au point CUE"
          aria-label={`Platine ${deckId}: Aller au point CUE${cuePoint !== null ? ` (${formatTime(cuePoint)})` : ' (non défini)'}`}
        >
          <SkipBack className="w-4 h-4 sm:w-5 sm:h-5" />
        </Button>

        <Button
          onClick={handlePlayPauseClick}
          size="lg"
          className={`w-16 h-16 sm:w-20 sm:h-20 min-w-[44px] min-h-[44px] rounded-full ${
            isActive
              ? 'bg-gradient-to-br from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700 focus:ring-4 focus:ring-purple-400/50'
              : 'bg-slate-700 hover:bg-slate-600 focus:ring-4 focus:ring-slate-400/50'
          } shadow-lg touch-manipulation transition-all`}
          disabled={!track}
          aria-label={`Platine ${deckId}: ${isPlaying ? 'Mettre en pause' : 'Lecture'}${track ? ` - ${track.title}` : ''}`}
          aria-pressed={isPlaying}
        >
          {isPlaying ? (
            <Pause className="w-6 h-6 sm:w-8 sm:h-8 text-white" aria-hidden="true" />
          ) : (
            <Play className="w-6 h-6 sm:w-8 sm:h-8 text-white ml-1" aria-hidden="true" />
          )}
        </Button>

        <Button
          onClick={() => onCue && onCue(0)}
          size="lg"
          variant="outline"
          className="w-12 h-12 sm:w-14 sm:h-14 min-w-[44px] min-h-[44px] rounded-full border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10 touch-manipulation"
          disabled={!track}
          title="Retour au début (CUE)"
          aria-label={`Platine ${deckId}: Retour au début`}
        >
          <span className="text-xl font-bold">CUE</span>
        </Button>
      </div>

      {/* Pitch Control - Accessible */}
      <div className="mb-3 sm:mb-4 bg-dark-input/30 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="text-xs sm:text-sm font-semibold text-purple-200">PITCH / TEMPO</span>
            {pitchInfo && pitchInfo.currentBPM && (
              <span className="text-xs text-purple-400/70 font-mono">
                {pitchInfo.currentBPM} BPM
              </span>
            )}
          </div>
          <span className="text-xs sm:text-sm font-mono text-sunset-orange">
            {pitch > 0 ? '+' : ''}{pitch.toFixed(1)}%
          </span>
        </div>
        <Slider
          value={[pitch]}
          onValueChange={(value) => onPitchChange && onPitchChange(value[0])}
          min={-8}
          max={8}
          step={0.1}
          className="w-full min-h-[44px] touch-manipulation"
          aria-label={`Platine ${deckId}: Contrôle du pitch`}
          aria-valuemin={-8}
          aria-valuemax={8}
          aria-valuenow={pitch}
          aria-valuetext={`${pitch > 0 ? '+' : ''}${pitch.toFixed(1)}%`}
        />
        <div className="flex justify-between text-xs text-sunset-orange/50 mt-1">
          <span>-8%</span>
          <span>0%</span>
          <span>+8%</span>
        </div>
      </div>

      {/* Deck Volume Control - Accessible */}
      <div className="mb-4 sm:mb-6 bg-dark-input/30 rounded-lg p-3 sm:p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs sm:text-sm font-semibold text-purple-200">VOLUME DECK {deckId}</span>
          <span className="text-xs sm:text-sm font-mono text-sunset-orange">
            {volume}%
          </span>
        </div>
        <Slider
          value={[volume]}
          onValueChange={(value) => onVolumeChange && onVolumeChange(value[0])}
          min={0}
          max={100}
          step={1}
          className="w-full min-h-[44px] touch-manipulation"
          aria-label={`Platine ${deckId}: Volume de la platine`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={volume}
          aria-valuetext={`${volume}%`}
        />
        <div className="flex justify-between text-xs text-sunset-orange/50 mt-1">
          <span>0%</span>
          <span>50%</span>
          <span>100%</span>
        </div>
      </div>

      {/* Cue Button */}
      {/* Hot Cues - Désactivé pour MVP, prévu pour V2 */}
      {/* <div className="grid grid-cols-4 gap-2 mb-4">
        {[1, 2, 3, 4].map((num) => (
          <Button
            key={num}
            size="sm"
            variant="outline"
            className="border-sunset-orange/30 text-purple-400 hover:bg-purple-500/10 h-10"
            disabled={!track}
          >
            {num}
          </Button>
        ))}
      </div> */}

      {/* Loop, Loop-Roll & Sync Controls */}
      <div className="bg-dark-input/30 rounded-lg p-2 sm:p-3 space-y-3">
        {/* Standard Loop Buttons */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] sm:text-xs font-semibold text-purple-200 uppercase tracking-wider">
              Boucle
            </span>
            {loopSize && (
              <span className="text-[9px] sm:text-[10px] text-sunset-orange/70 font-mono">
                {loopSize} temps
              </span>
            )}
          </div>
          <LoopButtons
            currentSize={loopSize}
            onLoopChange={(size) => onLoopChange && onLoopChange(deckId, size)}
            disabled={!track || !isPlaying}
          />
        </div>
        
        {/* Loop-Roll Buttons (Temporary loops with slip mode) */}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[10px] sm:text-xs font-semibold text-purple-400 uppercase tracking-wider">
              Loop-Roll
            </span>
            {loopRollSize && (
              <span className="text-[9px] sm:text-[10px] text-purple-300/70 font-mono animate-pulse">
                ACTIF
              </span>
            )}
          </div>
          <LoopRollButtons
            activeSize={loopRollSize}
            onLoopRollStart={(size) => onLoopRollStart && onLoopRollStart(deckId, size)}
            onLoopRollEnd={() => onLoopRollEnd && onLoopRollEnd(deckId)}
            disabled={!track || !isPlaying}
          />
        </div>
        
        {/* Sync Button */}
        <Button
          onClick={() => onSync(deckId)}
          variant="outline"
          size="sm"
          className="w-full border-blue-500/30 text-blue-400 hover:bg-blue-500/10 font-bold h-7 sm:h-8 text-[10px] sm:text-xs"
          disabled={!track || !pitchInfo || !pitchInfo.originalBPM}
          title={pitchInfo && pitchInfo.originalBPM ? 
            `Synchroniser BPM avec deck opposé` : 
            'BPM requis pour synchronisation'}
        >
          <Zap className="w-3 h-3 mr-1" />
          SYNC
        </Button>
      </div>

    </div>
  );
};

export default DeckControls;
